/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import React, {ReactNode} from 'react';
import Props from './Props';
import State from './State';
import FormGroup from '../FormGroup';
import FormControl from '../FormControl';
import {ProgressBar} from 'react-bootstrap';
import {ConstraintViolationDTO} from '../../model/validation';
import styles from './styles';
import {css} from 'aphrodite/no-important';

class PhoneVerificationCodeInput extends React.Component<Props, State> {

	static defaultProps: Props = {
		autoFocus: false,
		maximumDurationInSeconds: null,
		onVerificationCodeChange: () => {
			// do nothing
		},
		onVerificationCodeEnter: () => {
			// do nothing
		},
		verificationCode: '',
		verificationStopped: false,
		violations: []
	};

	private timer: NodeJS.Timeout | null;

	constructor(props: Props) {
		super(props);

		this.timer = null;

		this.state = {timeRemainingInSeconds: null, verificationCode: ''};
	}

	componentDidMount(): void {
		const props: Readonly<Props> = this.props;

		if (props.maximumDurationInSeconds !== null)
			this.startTimer(props.maximumDurationInSeconds);
	}

	componentDidUpdate(prevProps: Props): void {
		const props: Readonly<Props> = this.props;

		const newMaximumDurationDefined = props.maximumDurationInSeconds !== null;
		const currentMaximumDurationDefined = prevProps.maximumDurationInSeconds !== null;

		if (newMaximumDurationDefined && !currentMaximumDurationDefined)
			this.startTimer(Number(props.maximumDurationInSeconds));

		if (!newMaximumDurationDefined && currentMaximumDurationDefined)
			this.stopTimer();
	}

	componentWillUnmount(): void {
		this.stopTimer();
	}

	startTimer = (maximumDurationInSeconds: number): void => {
		this.stopTimer();

		this.setState({timeRemainingInSeconds: maximumDurationInSeconds, verificationCode: ''});

		this.timer = setInterval(
			() => {
				const state: Readonly<State> = this.state;
				this.setState({timeRemainingInSeconds: Math.max(0, (state.timeRemainingInSeconds || 0) - 1)});
			},
			1000
		);
	};

	stopTimer = (): void => {
		if (!this.timer)
			return;

		clearInterval(this.timer);

		this.timer = null;
	};

	handleVerificationCodeChange = (event: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
		const props: Readonly<Props> = this.props;

		props.onVerificationCodeChange(String(event.currentTarget.value).trim());
	};

	render(): ReactNode {
		const props: Readonly<Props> = this.props;
		const state: Readonly<State> = this.state;

		const verificationCode: string = props.verificationCode;
		const autoFocus: boolean = props.autoFocus;
		const verificationStopped: boolean = props.verificationStopped;
		const violations: ConstraintViolationDTO[] = props.violations;
		const onVerificationCodeEnter: () => void = props.onVerificationCodeEnter;

		let maximumDurationInSeconds: number = props.maximumDurationInSeconds || 1;
		let timeRemainingInSeconds: number = state.timeRemainingInSeconds !== null ? state.timeRemainingInSeconds : maximumDurationInSeconds;
		if (verificationStopped) {
			maximumDurationInSeconds = 1;
			timeRemainingInSeconds = 0;
		}

		return (
			<React.Fragment>
				<FormGroup controlId="verificationCode" label="Verificatiecode:" violations={violations}>
					<FormControl
						type="number"
						value={verificationCode}
						placeholder="Verificatiecode"
						autoFocus={autoFocus}
						disabled={verificationStopped}
						isInvalid={violations.length > 0}
						onChange={this.handleVerificationCodeChange}
						onEnter={onVerificationCodeEnter}/>
				</FormGroup>
				<ProgressBar
					animated={true}
					now={timeRemainingInSeconds}
					max={maximumDurationInSeconds}
					className={css(styles.progressBar)}/>
			</React.Fragment>
		);
	}
}

export default PhoneVerificationCodeInput;
